import React from 'react';

import useQueryParams from 'Hooks/useQueryParams';
import {
  FACET_PLAN_OUTCOME_PRIORITIZATION_ITEM,
  FACET_PLAN_SAC,
  FACET_PLAN_SIDEBAR_QUERY_PARAM,
  FACET_PLAN_VIEW_SIDEBAR_ASSUMPTIONS,
  FACET_PLAN_VIEW_SIDEBAR_EXPENSES,
  FACET_PLAN_VIEW_SIDEBAR_BALANCE_SHEET,
  FACET_PLAN_VIEW_SIDEBAR_LOGS,
  FACET_PLAN_VIEW_SIDEBAR_SUMMARY,
  FACET_PLAN_VIEW_SIDEBAR_RESOURCES,
  FACET_PLAN_VIEW_SIDEBAR_DESIRED_OUTCOMES,
  FACET_PLAN_VIEW_SIDEBAR_AUTOMATION_SUGGESTIONS,
  FACET_PLAN_CURRENT_MONTH_CONFIGURATIONS,
  FACET_PLAN_VIEW_SIDEBAR_JOBS,
  FACET_PLAN_SAC_OUTCOMES,
} from 'Views/Page/Content/Templates/WhatIfTemplate/Components/Sidebar/consts';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box/Box';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import Outcomes
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/LeftSide/Content/DetailPicker/Details/Templates/Outcomes';
import BaseOverride from 'Views/Page/Content/Templates/WhatIfTemplate/BaseOverride';
import Assumptions
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/LeftSide/Content/DetailPicker/Details/Templates/Assumptions';
import ExpenseSection
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/LeftSide/Content/DetailPicker/Details/Content/ExpenseSection';
import BalanceSheetPicker
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/LeftSide/Content/DetailPicker/Details/Templates/BalanceSheetPicker';
import AvailableResourcesPicker
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/LeftSide/Content/DetailPicker/Details/Templates/ResourcesPicker';
import DesiredOutcomesPicker
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/LeftSide/Content/DetailPicker/Details/Templates/DesiredOutcomesPicker';
import AutomationSuggestionsPicker
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/LeftSide/Content/DetailPicker/Details/Templates/AutomationSuggestionsPicker';
import CurrentMonthConfiguration
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/LeftSide/Content/DetailPicker/Details/Templates/CurrentMonthConfiguration';
import SummaryContainer from './Container';
import { SummarySidebarContentProvider } from './Context';
import LogsPicker from './Content/LogsPicker';
import SmartAllocationCustomizationPicker from './Templates/SmartAllocationCustomizationPicker';
import JobsPicker from './Templates/JobsPicker';

const TemplateComponent = () => {
  const [params, setQueryParams] = useQueryParams();

  let currentTab = params.get(FACET_PLAN_SIDEBAR_QUERY_PARAM);
  const { pageContentResource, pageContentCollection } = usePageContentContext();

  const tabs = pageContentResource?.attr('sidebarTabs');
  const tabResource = tabs?.resources().list().find(t => t.attr('key') === currentTab);

  // If the tab is not found, default to the first tab
  if (!tabResource) {
    currentTab = tabs?.resources().head().attr('key');
    params.set(FACET_PLAN_SIDEBAR_QUERY_PARAM, currentTab);
    setQueryParams(params);
  }

  const identifier = tabResource?.relationship('tabData')?.data?.[0];
  const tabDataResource = identifier ? pageContentCollection?.included().findResource(identifier) : null;

  switch (currentTab) {
    case FACET_PLAN_OUTCOME_PRIORITIZATION_ITEM: {
      return (
        <Box key={tabDataResource.instanceId()}>
          {tabDataResource && <MetaFormInline resource={tabDataResource} layout={Outcomes} noIndividualSubmitButtons />}
        </Box>
      );
    }
    case FACET_PLAN_SAC_OUTCOMES: // fallthrough
    case FACET_PLAN_SAC: {
      return (<SmartAllocationCustomizationPicker key={tabResource.instanceId()} tabResource={tabResource} />);
    }
    case FACET_PLAN_VIEW_SIDEBAR_ASSUMPTIONS: {
      return (
        <Box key={tabDataResource.instanceId()}>
          {tabDataResource && <MetaFormInline
            noIndividualSubmitButtons
            resource={tabDataResource}
            layout={Assumptions}
            metaOverrideSet={BaseOverride}
          />}
        </Box>
      );
    }
    case FACET_PLAN_VIEW_SIDEBAR_EXPENSES: {
      return (
        <Box key={tabDataResource.instanceId()}>
          {tabDataResource && (
            <SummarySidebarContentProvider>
              <React.Fragment key={tabDataResource.instanceId()}>
                <ExpenseSection resource={tabDataResource.attr('inputs').resources().head()} />
              </React.Fragment>
            </SummarySidebarContentProvider>
          )}
        </Box>
      );
    }
    case FACET_PLAN_VIEW_SIDEBAR_BALANCE_SHEET: {
      return (
        <SummarySidebarContentProvider>
          <BalanceSheetPicker tabResource={tabResource} />
        </SummarySidebarContentProvider>
      );
    }
    case FACET_PLAN_VIEW_SIDEBAR_LOGS: {
      return (
        <Box key={tabDataResource.instanceId()} style={{ height: '100%' }}>
          {tabDataResource && (
            <LogsPicker resource={tabDataResource} />
          )}
        </Box>
      );
    }
    case FACET_PLAN_VIEW_SIDEBAR_RESOURCES: {
      return (
        <Box key={tabDataResource.instanceId()} style={{ height: '100%' }}>
          {tabDataResource && (
          <AvailableResourcesPicker tabResource={tabResource} />
          )}
        </Box>
      );
    }
    case FACET_PLAN_VIEW_SIDEBAR_DESIRED_OUTCOMES: {
      return (
        <Box key={tabDataResource.instanceId()} style={{ height: '100%' }}>
          {tabDataResource && (
            <DesiredOutcomesPicker tabResource={tabResource} />
          )}
        </Box>
      );
    }
    case FACET_PLAN_VIEW_SIDEBAR_AUTOMATION_SUGGESTIONS: {
      return (
        <AutomationSuggestionsPicker tabResource={tabResource} />
      );
    }
    case FACET_PLAN_CURRENT_MONTH_CONFIGURATIONS: {
      return (
        <Box key={tabDataResource.instanceId()}>
          {tabDataResource && <MetaFormInline
            noIndividualSubmitButtons
            resource={tabDataResource}
            layout={CurrentMonthConfiguration}
            metaOverrideSet={BaseOverride}
          />}
        </Box>
      );
    }
    case FACET_PLAN_VIEW_SIDEBAR_JOBS: {
      return (
        <Box key={tabDataResource.instanceId()}>
          {tabDataResource && (
            <JobsPicker tabResource={tabResource} />
          )}
        </Box>
      );
    }
    case FACET_PLAN_VIEW_SIDEBAR_SUMMARY:
    default: {
      return (
        <SummarySidebarContentProvider>
          <SummaryContainer />
        </SummarySidebarContentProvider>
      );
    }
  }
};

export default TemplateComponent;
